/**
 * RichTextEditor - Collaborative Tiptap editor with Yjs
 *
 * This component connects Tiptap to the existing Yjs infrastructure from Phase 3:
 * - Uses Y.Doc XmlFragment for rich text content (separate from canvas 'nodes')
 * - Uses Awareness for cursor/selection sync
 * - Disables StarterKit's undoRedo in favor of Yjs-based undo/redo
 *
 * IMPORTANT: The editor uses ydoc.getXmlFragment('richtext'), NOT 'document'.
 * Phase 3 defined 'document' as a Y.Map, so we use a different key.
 * This keeps rich text content separate from whiteboard elements.
 *
 * Key Integration Points:
 * - ydoc: From useYDoc hook (Phase 3) - provides the shared document
 * - awareness: From useYjsProvider hook (Phase 3) - provides cursor sync
 * - currentUser: Contains id, name, color for cursor display
 *
 * Usage:
 * ```tsx
 * <RichTextEditor
 *   ydoc={ydoc}
 *   awareness={awareness}
 *   currentUser={{ id: 'user-1', name: 'Alice', color: '#4dabf7' }}
 *   placeholder="Type / for commands..."
 * />
 * ```
 */

import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Collaboration from '@tiptap/extension-collaboration';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
// Table extensions for structured data
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
// Code block with syntax highlighting
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
// Slash commands for block insertion
import { SlashCommands } from './extensions/slash-commands';
// Comment extension for inline comments
import { Comment } from './extensions/comment';
// AI attribution extension for marking AI-generated content
import { AIGenerated } from './extensions/ai-generated';
// Mention extension for @mentions
import Mention from '@tiptap/extension-mention';
import {
  mentionSuggestion,
  type MentionSuggestionMode,
  type NodeMentionSuggestion,
} from './extensions/mention-suggestion';
import { CollaborationCursor } from './extensions/collaboration-cursor';
// Additional formatting marks for BubbleToolbar
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';

// Import BubbleToolbar for text formatting
import { BubbleToolbar } from './BubbleToolbar';
// Import CommentPopover for viewing/replying to comments
import { CommentPopover } from './CommentPopover';
// Import AIPromptModal for AI slash commands
import { AIPromptModal } from './AIPromptModal';

/**
 * Create lowlight instance with common languages
 *
 * The 'common' bundle includes: javascript, typescript, python, css, html, json,
 * markdown, bash, xml, yaml, and more. This covers most use cases without
 * loading all ~190 languages that lowlight supports.
 */
const lowlight = createLowlight(common);

// Import editor styles - these are critical for cursor rendering
import '../../styles/editor.css';

/**
 * Props for the RichTextEditor component
 */
interface RichTextEditorProps {
  /**
   * Y.Doc from useYDoc hook (Phase 3)
   * The Yjs document that syncs across all collaborators
   */
  ydoc: Y.Doc;

  /**
   * Awareness from useYjsProvider hook (Phase 3)
   * Used for cursor position and selection sync
   */
  awareness: Awareness;

  /**
   * Current user info for cursor display
   * - id: Unique user identifier
   * - name: Display name shown on cursor label
   * - color: CSS color for cursor (e.g., '#4dabf7')
   */
  currentUser: {
    id: string;
    name: string;
    color: string;
  };

  /**
   * Board ID for comments (required for inline comments)
   * Used to store comments in D1 database
   */
  boardId?: string;

  /**
   * Optional placeholder text shown when editor is empty
   * Defaults to 'Type / for commands...'
   */
  placeholder?: string;

  /**
   * Optional CSS class for the container element
   */
  className?: string;

  /**
   * Optional node mention suggestions for @ placeholders
   */
  mentionSuggestions?: NodeMentionSuggestion[];

  /**
   * Mention mode for the suggestion list
   */
  mentionMode?: MentionSuggestionMode;

  /**
   * Yjs fragment field name (defaults to 'richtext')
   * IMPORTANT: Each BlockNode should use a unique field name
   * like 'richtext-nodeId' to avoid sharing content
   */
  fieldName?: string;

  /**
   * WebSocket ref for sending AI requests to Board DO (Phase 7)
   * Used by AI slash commands and BubbleToolbar transforms
   */
  wsRef?: React.RefObject<WebSocket | null>;
}

export interface RichTextEditorHandle {
  insertMention: (id: string, label: string) => void;
}

/**
 * RichTextEditor - Main collaborative text editor component
 *
 * Integrates Tiptap with Yjs for real-time collaboration:
 * 1. StarterKit provides basic formatting (bold, italic, lists, etc.)
 * 2. Collaboration extension syncs content via Y.Doc
 * 3. CollaborationCursor shows where other users are typing
 * 4. Placeholder shows hint text when editor is empty
 */
export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(function RichTextEditor({
  ydoc,
  awareness,
  currentUser,
  boardId,
  placeholder = 'Type / for commands...',
  className = '',
  mentionSuggestions,
  mentionMode,
  fieldName = 'richtext',
  wsRef,
}: RichTextEditorProps, ref) {
  const resolvedMentionMode = mentionMode ?? (mentionSuggestions !== undefined ? 'nodes' : 'users');
  // ---------------------------------------------------------------------------
  // COMMENT STATE
  // ---------------------------------------------------------------------------

  // Track active comment popover (shown when clicking highlighted text)
  const [activeComment, setActiveComment] = useState<{
    id: string;
    position: { top: number; left: number };
  } | null>(null);

  // ---------------------------------------------------------------------------
  // AI PROMPT MODAL STATE
  // ---------------------------------------------------------------------------

  // Track AI prompt modal state (opened by /ai and /continue slash commands)
  const [aiPromptState, setAIPromptState] = useState<{
    isOpen: boolean;
    mode: 'generate' | 'continue';
    context?: string;
    cursorPosition: number;
  }>({
    isOpen: false,
    mode: 'generate',
    cursorPosition: 0,
  });

  // ---------------------------------------------------------------------------
  // COMMENT HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Handle click on a comment highlight.
   * Opens the CommentPopover at the clicked position.
   */
  const handleCommentClick = useCallback((commentId: string, rect: DOMRect) => {
    setActiveComment({
      id: commentId,
      position: { top: rect.bottom + 8, left: rect.left },
    });
  }, []);

  // ---------------------------------------------------------------------------
  // EDITOR INITIALIZATION
  // ---------------------------------------------------------------------------

  // Create a provider wrapper for CollaborationCursor
  // The custom extension at ./extensions/collaboration-cursor expects { awareness }
  const provider = useMemo(() => {
    if (!awareness) return null;
    return { awareness };
  }, [awareness]);

  // Build extensions array
  const extensions = useMemo(() => {
    const baseExtensions = [
      // ==== CORE EDITOR FUNCTIONALITY ====
      // StarterKit bundles essential extensions:
      // - Paragraphs, headings (H1-H6)
      // - Bold, italic, strike, code
      // - Bullet lists, ordered lists
      // - Code blocks, blockquotes
      // - Horizontal rule, hard break
      //
      // CRITICAL: Disable built-in undo/redo!
      // The Collaboration extension provides Yjs-based undo/redo that
      // respects collaborative editing (only undoes YOUR changes, not others')
      // NOTE: In Tiptap 3.x, the option is 'undoRedo' (was 'history' in 2.x)
      StarterKit.configure({
        undoRedo: false, // Let Yjs handle undo/redo
        codeBlock: false, // Use CodeBlockLowlight instead for syntax highlighting
      }),

      // ==== YJS DOCUMENT SYNCHRONIZATION ====
      // Syncs editor content to Y.Doc XmlFragment
      // Uses unique field per node (e.g., 'richtext-nodeId')
      // NOTE: Cannot use 'document' because Phase 3 defined it as Y.Map
      // This keeps rich text content separate from canvas 'nodes' Y.Map
      Collaboration.configure({
        document: ydoc,
        field: fieldName, // Creates ydoc.getXmlFragment(fieldName)
      }),

      // ==== PLACEHOLDER TEXT ====
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (node.type.name === 'heading') {
            return 'Heading';
          }
          return placeholder;
        },
      }),

      // ==== LINK EXTENSION ====
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'editor-link' },
      }),

      // ==== IMAGE EXTENSION ====
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: { class: 'editor-image' },
      }),

      // ==== CODE BLOCK WITH SYNTAX HIGHLIGHTING ====
      CodeBlockLowlight.configure({
        lowlight,
        defaultLanguage: 'plaintext',
      }),

      // ==== ADDITIONAL FORMATTING MARKS ====
      Underline,
      Highlight.configure({ multicolor: true }),
      TextStyle,
      Color,

      // ==== TABLE EXTENSIONS ====
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,

      // ==== SLASH COMMANDS ====
      SlashCommands,

      // ==== INLINE COMMENTS ====
      Comment.configure({
        onCommentClick: handleCommentClick,
      }),

      // ==== @MENTIONS ====
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: mentionSuggestion,
      }),

      // ==== AI ATTRIBUTION ====
      // Marks AI-generated text with violet underline and hover tooltip
      // Enables transparency about which content was written by AI
      AIGenerated,
    ];

    // ==== REMOTE CURSOR/SELECTION RENDERING ====
    // Only add CollaborationCursor when provider is available (awareness is connected)
    if (provider) {
      baseExtensions.push(
        CollaborationCursor.configure({
          provider,
          user: {
            name: currentUser.name,
            color: currentUser.color,
          },
        })
      );
    }

    return baseExtensions;
  }, [ydoc, provider, currentUser, placeholder, handleCommentClick]);

  // Initialize Tiptap editor with extensions
  const editor = useEditor({
    extensions,
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
  }, [extensions]);

  // Sync mention suggestions into editor storage for suggestion menu
  useEffect(() => {
    if (!editor) return;

    const storage = editor.storage as {
      mentionMode?: MentionSuggestionMode;
      nodeMentions?: NodeMentionSuggestion[];
    };

    storage.mentionMode = resolvedMentionMode;

    storage.nodeMentions = mentionSuggestions ?? [];
  }, [editor, mentionSuggestions, resolvedMentionMode]);

  const mentionContentKey = useMemo(() => {
    if (!mentionSuggestions) return '';
    return mentionSuggestions
      .map((item) => `${item.id}:${item.content || ''}:${item.imageUrl || ''}`)
      .join('|');
  }, [mentionSuggestions]);

  // Update mention display to show live node content while keeping placeholders intact
  useEffect(() => {
    if (!editor) return;
    if (resolvedMentionMode !== 'nodes') return;
    if (editor.isDestroyed) return;

    const suggestions = mentionSuggestions ?? [];
    const suggestionById = new Map(suggestions.map((item) => [item.id, item]));

    let rafId = 0;
    const applyMentions = () => {
      if (editor.isDestroyed) return;
      let editorElement: HTMLElement | null = null;
      try {
        editorElement = editor.view.dom as HTMLElement;
      } catch {
        return;
      }

      if (!editorElement) return;
      const mentionElements = editorElement.querySelectorAll('.mention, [data-type="mention"]');

      mentionElements.forEach((el) => {
        const mentionEl = el as HTMLElement;
        const dataId = mentionEl.getAttribute('data-id') || '';
        const storedLabel = mentionEl.getAttribute('data-label') || '';
        const textLabel = mentionEl.textContent?.replace(/^@/, '').trim() || '';

        let suggestion = dataId ? suggestionById.get(dataId) : undefined;
        if (!suggestion && storedLabel) {
          suggestion = suggestions.find((item) => item.label.toLowerCase() === storedLabel.toLowerCase());
        }
        if (!suggestion && textLabel) {
          suggestion = suggestions.find((item) => item.label.toLowerCase() === textLabel.toLowerCase());
        }

        if (!suggestion) {
          mentionEl.classList.remove('has-content');
          mentionEl.querySelectorAll('.mention-content-overlay').forEach((node) => node.remove());
          return;
        }

        const hasImage = typeof suggestion.imageUrl === 'string' && suggestion.imageUrl.trim() !== '';
        const textContent = typeof suggestion.content === 'string' ? suggestion.content.trim() : '';
        const hasText = textContent !== '';
        const hasContent = hasImage || hasText;

        mentionEl.setAttribute('data-node-id', suggestion.id);

        let overlay = mentionEl.querySelector('.mention-content-overlay') as HTMLElement | null;
        if (!hasContent) {
          if (overlay) overlay.remove();
          mentionEl.classList.remove('has-content');
          return;
        }

        mentionEl.classList.add('has-content');

        if (!overlay) {
          overlay = document.createElement('span');
          overlay.className = 'mention-content-overlay';
          overlay.setAttribute('contenteditable', 'false');
          mentionEl.appendChild(overlay);
        }

        const signature = hasImage ? `img:${suggestion.imageUrl}` : `txt:${textContent}`;
        if (overlay.getAttribute('data-content-sig') === signature) {
          return;
        }

        overlay.setAttribute('data-content-sig', signature);
        while (overlay.firstChild) {
          overlay.removeChild(overlay.firstChild);
        }

        if (hasImage && suggestion.imageUrl) {
          const img = document.createElement('img');
          img.src = suggestion.imageUrl;
          img.alt = suggestion.label || 'Image';
          img.className = 'mention-image';
          overlay.appendChild(img);
        } else if (hasText) {
          overlay.textContent = textContent;
        }
      });
    };

    const schedule = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        applyMentions();
      });
    };

    schedule();
    editor.on('update', schedule);

    return () => {
      editor.off('update', schedule);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [editor, mentionSuggestions, mentionContentKey, resolvedMentionMode]);

  useImperativeHandle(ref, () => ({
    insertMention: (id: string, label: string) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'mention',
            attrs: { id, label },
          },
          { type: 'text', text: ' ' },
        ])
        .run();
    },
  }), [editor]);

  // ---------------------------------------------------------------------------
  // AI PROMPT MODAL EVENT LISTENER
  // ---------------------------------------------------------------------------

  /**
   * Listen for aiPromptOpen events from slash commands.
   * When /ai or /continue is selected, the slash command emits this event
   * so we can show the AIPromptModal.
   *
   * NOTE: We cast to 'any' because Tiptap's EditorEvents type doesn't include
   * custom events. This is a known limitation - custom events work at runtime
   * but TypeScript doesn't recognize them without module augmentation.
   */
  useEffect(() => {
    if (!editor) return;

    const handleAIPromptOpen = ({
      mode,
      context,
    }: {
      mode: 'generate' | 'continue';
      context?: string;
    }) => {
      setAIPromptState({
        isOpen: true,
        mode,
        context,
        cursorPosition: editor.state.selection.from,
      });
    };

    // Listen for the custom event from slash commands
    // Use type assertion since Tiptap doesn't expose custom event types
    (editor as any).on('aiPromptOpen', handleAIPromptOpen);

    // Cleanup on unmount or editor change
    return () => {
      (editor as any).off('aiPromptOpen', handleAIPromptOpen);
    };
  }, [editor]);

  // ---------------------------------------------------------------------------
  // AI PROMPT HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Handle AI prompt submission.
   *
   * Sends AI request via WebSocket to Board DO, which calls AI Gateway
   * and streams the response back via Y.Doc updates.
   *
   * Flow:
   * 1. Build AI request message with prompt and context
   * 2. Send via WebSocket to Board DO
   * 3. Board DO calls AI Gateway and streams response
   * 4. Y.Doc updates automatically appear in editor
   * 5. AIGenerated mark applied for attribution
   */
  const handleAIPromptSubmit = useCallback(
    async (prompt: string) => {
      if (!editor) return;

      // Check if WebSocket is available
      if (!wsRef?.current || wsRef.current.readyState !== WebSocket.OPEN) {
        console.warn('[RichTextEditor] WebSocket not connected for AI request');
        // Fallback: show error message in editor
        editor.chain().focus().insertContent('[AI unavailable - not connected]').run();
        setAIPromptState((prev) => ({ ...prev, isOpen: false }));
        return;
      }

      // Build the AI request message
      // For 'continue' mode, include context before cursor
      // For 'generate' mode, just use the prompt
      const aiRequest = {
        type: 'ai_request',
        action: aiPromptState.mode === 'continue' ? 'expand' : 'generate',
        targetType: 'document',
        targetField: fieldName, // The Y.XmlFragment field (e.g., 'richtext')
        context:
          aiPromptState.mode === 'continue' && aiPromptState.context
            ? `${aiPromptState.context}\n\n${prompt}`
            : prompt,
      };

      // Send via WebSocket to Board DO
      wsRef.current.send(JSON.stringify(aiRequest));

      console.log('[RichTextEditor] AI request sent:', aiRequest.action, prompt.slice(0, 50) + '...');

      // Close the modal
      // The AI response will stream back via Y.Doc updates automatically
      setAIPromptState((prev) => ({ ...prev, isOpen: false }));
    },
    [editor, wsRef, aiPromptState, fieldName]
  );

  /**
   * Handle AI prompt modal close.
   * Closes the modal without taking any action.
   */
  const handleAIPromptClose = useCallback(() => {
    setAIPromptState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  /**
   * Handle comment resolution.
   * Removes the comment mark from the editor when thread is resolved.
   *
   * Note: Defined after useEditor so editor is available in closure.
   */
  const handleCommentResolve = useCallback(
    (commentId: string) => {
      if (editor) {
        editor.commands.unsetComment(commentId);
      }
    },
    [editor]
  );

  // Handle loading state while editor initializes
  // This prevents flash of unstyled content
  if (!editor) {
    return (
      <div className={`rich-text-editor-loading ${className}`}>
        Loading editor...
      </div>
    );
  }

  // Render the editor
  return (
    <div className={`rich-text-editor ${className}`} data-mention-mode={resolvedMentionMode}>
      {/* BubbleToolbar - floating toolbar that appears on text selection */}
      {/* Pass wsRef and fieldName for AI transform requests via WebSocket */}
      <BubbleToolbar editor={editor} boardId={boardId} wsRef={wsRef} fieldName={fieldName} />
      {/* EditorContent renders the actual editable area */}
      <EditorContent editor={editor} />

      {/* CommentPopover - shows when clicking highlighted comment text */}
      {activeComment && boardId && (
        <CommentPopover
          commentId={activeComment.id}
          boardId={boardId}
          position={activeComment.position}
          onClose={() => setActiveComment(null)}
          onResolve={handleCommentResolve}
        />
      )}

      {/* AIPromptModal - shows when /ai or /continue slash command is selected */}
      <AIPromptModal
        isOpen={aiPromptState.isOpen}
        mode={aiPromptState.mode}
        initialContext={aiPromptState.context}
        onSubmit={handleAIPromptSubmit}
        onClose={handleAIPromptClose}
      />
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

// Re-export Editor type for consumers who need to access editor instance
// This is useful for implementing toolbar buttons that call editor commands
export type { Editor } from '@tiptap/react';
